import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList } from "@fortawesome/pro-regular-svg-icons/faList";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons/faArrowRight";
import axios from "axios";
import { useConfig } from "@peracto/peracto-config";
import { HorizontalScrollbar } from "../../../components/HorizontalScrollbar";

let Icon = () => (
    <div className="flex py-10 justify-center items-center w-full">
        <FontAwesomeIcon icon={faList} size="6x" />
    </div>
);

const MenuItemsComponent = ({
    state: {
        menuItemPath,
        categoryPath = "category",
        itemLimit = 4,
        heading = "",
    },
    isEditMode,
}) => {
    const config = useConfig();
    const API_URL = config.get("api");
    const [menuItemData, setMenuItemData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(false);
    const [showHorizontalScrollbar, setShowHorizontalScrollbar] =
        useState(false);
    useEffect(() => {
        if (menuItemPath) {
            const loadMenuItems = async () => {
                setShowHorizontalScrollbar(false);
                setLoadingError(false);
                const path =
                    menuItemPath[0] === "/"
                        ? menuItemPath.substr(1)
                        : menuItemPath;
                try {
                    axios
                        .get(`${API_URL}/menu-items-children-by-path/${path}`)
                        .then((res) => {
                            const data = res.data && res.data["hydra:member"];

                            if (data) {
                                setMenuItemData(data);
                                setIsLoading(false);
                            }
                        })
                        .catch((error) => {
                            setLoadingError(true);
                            return Promise.reject(error);
                        });
                } catch (e) {
                    console.error(e);
                    setLoadingError(true);
                }
            };

            loadMenuItems();
        }
    }, [API_URL, menuItemPath]);

    const containerRef = useRef(null);
    const scrollbarRef = useRef(null);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // --- Update button states ---
    const updateScrollButtons = () => {
        const container = containerRef.current;
        if (!container) return;
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
            container.scrollLeft <
                container.scrollWidth - container.clientWidth - 1,
        );
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let isDragging = false;
        let startX = 0;
        let scrollLeftStart = 0;
        let moved = false; // Track if user actually dragged

        const onMouseDown = (e) => {
            isDragging = true;
            moved = false;
            startX = e.pageX - container.offsetLeft;
            scrollLeftStart = container.scrollLeft;
            container.classList.add("cursor-grabbing");
            e.preventDefault();
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;
            const x = e.pageX - container.offsetLeft;
            const walk = x - startX;
            if (Math.abs(walk) > 2) moved = true; // small threshold to count as drag
            container.scrollLeft = scrollLeftStart - walk;
        };

        const onMouseUp = () => {
            isDragging = false;
            container.classList.remove("cursor-grabbing");
        };

        const onTouchStart = (e) => {
            isDragging = true;
            moved = false;
            startX = e.touches[0].pageX - container.offsetLeft;
            scrollLeftStart = container.scrollLeft;
        };

        const onTouchMove = (e) => {
            if (!isDragging) return;
            const x = e.touches[0].pageX - container.offsetLeft;
            const walk = x - startX;
            if (Math.abs(walk) > 2) moved = true;
            container.scrollLeft = scrollLeftStart - walk;
        };

        const onTouchEnd = () => (isDragging = false);

        // --- Prevent clicks on links if dragging ---
        const onClickCapture = (e) => {
            if (moved) {
                // prevent link clicks if dragging occurred
                e.preventDefault();
                e.stopPropagation();
            }
        };

        container.addEventListener("mousedown", onMouseDown);
        container.addEventListener("mousemove", onMouseMove);
        container.addEventListener("mouseup", onMouseUp);
        container.addEventListener("mouseleave", onMouseUp);
        container.addEventListener("touchstart", onTouchStart);
        container.addEventListener("touchmove", onTouchMove);
        container.addEventListener("touchend", onTouchEnd);

        container.addEventListener("click", onClickCapture, true); // capture phase

        return () => {
            container.removeEventListener("mousedown", onMouseDown);
            container.removeEventListener("mousemove", onMouseMove);
            container.removeEventListener("mouseup", onMouseUp);
            container.removeEventListener("mouseleave", onMouseUp);
            container.removeEventListener("touchstart", onTouchStart);
            container.removeEventListener("touchmove", onTouchMove);
            container.removeEventListener("touchend", onTouchEnd);
            container.removeEventListener("click", onClickCapture, true);
        };
    }, [menuItemData]);

    useEffect(() => {
        if (menuItemData) {
            setTimeout(() => {
                updateScrollButtons();
                setShowHorizontalScrollbar(true);
            }, 500);
        }
    }, [menuItemData]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        updateScrollButtons();

        container.addEventListener("scroll", updateScrollButtons);
        window.addEventListener("resize", updateScrollButtons);

        return () => {
            container.removeEventListener("scroll", updateScrollButtons);
            window.removeEventListener("resize", updateScrollButtons);
        };
    }, []);

    if (loadingError) {
        // this has padding so the element is at least clickable so it can be selected and edited
        return <div className="py-10 w-full"></div>;
    }

    const inStorefront = !window?.location?.pathname.includes("/content/edit/");

    return (
        <>
            {heading && (
                <h2 className="text-center !mb-[30px] !text-[30px] !leading-[36px] !lg:text-[40px] !lg:leading-[46px] font-extrabold">
                    {heading}
                </h2>
            )}

            <div className="relative">
                {/* Left Button */}
                <button
                    onClick={() => scrollbarRef.current?.scrollBy(-270)}
                    className="absolute z-[10] top-[100px] lg:-left-[25px] left-0 h-[50px] w-[50px] flex items-center justify-center bg-brand-quaternary/70 rounded-full hover:bg-brand-quaternary-darken text-brand-white disabled:opacity-50"
                    disabled={!canScrollLeft}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>

                {/* Right Button */}
                <button
                    onClick={() => scrollbarRef.current?.scrollBy(270)}
                    className="absolute z-[10] top-[100px] lg:-right-[25px] right-0 h-[50px] w-[50px] flex items-center justify-center bg-brand-quaternary/70 rounded-full hover:bg-brand-quaternary-darken text-brand-white disabled:opacity-50"
                    disabled={!canScrollRight}
                >
                    <FontAwesomeIcon icon={faArrowRight} />
                </button>

                <div
                    className="overflow-hidden flex gap-x-[15px] lg:gap-x-[30px]"
                    ref={containerRef}
                >
                    {menuItemPath ? (
                        <>
                            {menuItemData?.length > 0 && itemLimit > 0
                                ? menuItemData
                                      .slice(0, itemLimit)
                                      .map((menuItem, index) => {
                                          if (
                                              !menuItem.path ||
                                              menuItem.itemType === "branch"
                                          ) {
                                              return null;
                                          }
                                          let path =
                                              !menuItem.path.startsWith(
                                                  "http",
                                              ) && menuItemPath[0] !== "/"
                                                  ? `/${menuItem.path}`
                                                  : menuItem.path;

                                          if (
                                              menuItem.itemType ===
                                                  "category" &&
                                              !menuItem.path.startsWith(
                                                  `/${categoryPath}/`,
                                              )
                                          ) {
                                              path = `/${categoryPath}${path}`;
                                          }

                                          const image =
                                              menuItem.content?.image ||
                                              menuItem.image ||
                                              "/images/placeholder.png";

                                          const decodedImage = image
                                              ?.replaceAll("(", "%28")
                                              ?.replaceAll(")", "%29");

                                          return (
                                              <a
                                                  href={path}
                                                  onClick={(e) => {
                                                      e.preventDefault();
                                                      if (isEditMode) return;

                                                      window.location = path;
                                                  }}
                                                  className="content-menu-items__item flex flex-col min-w-[200px] lg:min-w-[307px] lg:max-w-[307px] flex-1 !no-underline"
                                                  key={`menu_item_${index}`}
                                              >
                                                  <div
                                                      className="flex flex-col relative gap-[15px]"
                                                      key={`link-row-${index}`}
                                                  >
                                                      <div className="aspect-[307/250] bg-brand-grey rounded-[10px] relative">
                                                          {decodedImage && (
                                                              <img
                                                                  src={
                                                                      decodedImage
                                                                  }
                                                                  className="absolute top-0 left-0 w-full h-full object-cover rounded-[10px]"
                                                                  alt="Background"
                                                              />
                                                          )}
                                                      </div>

                                                      {menuItem.linkText && (
                                                          <h2 className="!text-[18px] !leading-[24px] lg:!text-[24px] lg:!leading-[30px] text-brand-black mb-0">
                                                              {
                                                                  menuItem.linkText
                                                              }
                                                          </h2>
                                                      )}
                                                  </div>
                                              </a>
                                          );
                                      })
                                : !isLoading && "No Menu Items"}
                        </>
                    ) : inStorefront ? (
                        <></>
                    ) : (
                        <Icon />
                    )}
                </div>
            </div>

            {showHorizontalScrollbar && (
                <HorizontalScrollbar
                    containerRef={containerRef}
                    ref={scrollbarRef}
                />
            )}
        </>
    );
};

export default MenuItemsComponent;
